/**
 * El cobro de un cliente, con el guardado adentro.
 *
 * `DialogCobro` es solo el formulario. Esto le agrega lo que antes vivia en la
 * pantalla de Cuentas corrientes: las cuentas que reciben la plata, la
 * cotizacion, la mutacion y el aviso de cuanto le queda. Lo usan la lista de
 * deudores y la ficha del cliente, que desde que Clientes y Cuentas corrientes
 * son una sola seccion cobran en el mismo lugar donde se mira.
 */
import { formatear } from "@tora/shared";
import { DialogCobro } from "@/components/cobro/DialogCobro";
import type { CuentaFinanciera, DeudorFila } from "@/components/cobro/logica";
import { useConfig, useCuentasFinancieras, useRegistrarCobro } from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { aNumero } from "@/lib/tipos";

export interface CobroClienteProps {
  /** A quien se le cobra. `null` = el dialogo esta cerrado. */
  cliente: DeudorFila | null;
  onCerrar: () => void;
}

export function CobroCliente({ cliente, onCerrar }: CobroClienteProps): JSX.Element {
  const { data: cuentas } = useCuentasFinancieras();
  const { data: config } = useConfig();
  const registrar = useRegistrarCobro();

  const cotizacion = Number(config?.cotizacion_vigente ?? 0);
  // Sin cotizacion todavia (la config no llego), el dialogo no se abre: el
  // cobro de un cliente en dolares convierte al dibujarse y con 0 tiraba
  // «Cotizacion invalida (0)», que dejaba la app entera en blanco. Pasaba al
  // entrar en frio por `/clientes?id=X&cobrar=1`. Se abre solo apenas llega.
  const listo = cotizacion > 0;

  return (
    <DialogCobro
      cliente={listo ? cliente : null}
      cuentas={(cuentas?.filas ?? []) as CuentaFinanciera[]}
      cotizacion={cotizacion}
      guardando={registrar.isPending}
      onCerrar={onCerrar}
      onConfirmar={(pagos, clave, opciones) => {
        if (!cliente) return;
        registrar.mutate(
          // `clave`: si la red se corta despues de que el servidor cobro, el
          // reintento devuelve el mismo resultado en vez de cobrar de nuevo.
          // (0155) La cotizacion de la pantalla viaja: con ella la base mide la
          // deuda y el vuelto, y lo que se cobra es lo que se mostro.
          {
            cliente_id: cliente.cliente_id,
            pagos: pagos as never,
            clave,
            cotizacion,
            ...(opciones?.vuelto_propina ? { vuelto_propina: true } : {}),
          },
          {
            onSuccess: (r: any) => {
              const saldo = aNumero(r?.saldo_ars_hoy ?? r?.saldo ?? 0);
              const vuelto = aNumero(r?.vuelto ?? 0);
              mostrarExito(
                `Cobrado a ${cliente.nombre}`,
                vuelto > 0.009
                  ? opciones?.vuelto_propina
                    ? `Queda saldado. Dejó ${formatear(vuelto, "ARS")} de propina.`
                    : `Queda saldado. Vuelto: ${formatear(vuelto, "ARS")}.`
                  : saldo <= 0.009
                    ? "Queda saldado."
                    : `Le sigue quedando ${formatear(saldo, "ARS")}.`,
              );
              onCerrar();
            },
            onError: mostrarError,
          },
        );
      }}
    />
  );
}
